import { isEmpty, keyBy } from 'lodash/fp';

import {
  emptyTableCellLabel,
  getDefaultValueDisplay,
} from 'dashboard/apps/InquiryTemplateEditor/FieldSchemaEditorCard';
import { EMPTY_LABEL } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffSummary/InquiryTemplateVersionDiffSummary';
import {
  Column,
  HashDiff,
  VersionDiff,
  VersionDiffChangeType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import { changeTypeForColumn } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/utils';
import Table, {
  Cell,
  PropertyRow,
  Row,
  TableRowWithDiffTag,
} from 'dashboard/apps/InquiryTemplateEditor/VersionDiffTable';
import { TableRowWithDiffTagProps } from 'dashboard/apps/InquiryTemplateEditor/VersionDiffTable/TableRowWithDiffTag';
import { InquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import Card from 'design-system/components/Card';
import { Label } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { FIELD_POLICY_OPTIONS, FIELD_SCHEMA_TYPE_DISPLAY, FieldSchema } from 'lib/fields';
import useToggle from 'lib/hooks/useToggle';
import { assertNever } from 'lib/utils/types';

const generateValidationLabel = (field: FieldSchema): string => {
  const { config } = field;
  return 'max-char-length' in config && config['max-char-length'] != null
    ? `Max length: ${config['max-char-length']} char`
    : emptyTableCellLabel;
};

const generateRedactionPolicyLabel = (redactionPolicy: string | undefined) => {
  return (
    FIELD_POLICY_OPTIONS['redaction-policy'].find((policy) => policy.value === redactionPolicy)
      ?.label || EMPTY_LABEL
  );
};

const generateWritePolicyLabel = (writePolicy: string | undefined) => {
  return (
    FIELD_POLICY_OPTIONS['write-policy'].find((policy) => policy.value === writePolicy)?.label ||
    EMPTY_LABEL
  );
};

function FieldSchemaDisplay({
  field,
  fieldChanges,
  changeColumn,
}: {
  field: FieldSchema;
  fieldChanges?: HashDiff<string>;
  referenceLabelChangeType?: VersionDiffChangeType;
  changeColumn: Column;
}) {
  const { 'default-value': defaultValue, label, type, config } = field;
  const { 'write-policy': writePolicy, 'redaction-policy': redactionPolicy } = config;

  return (
    <>
      <PropertyRow textKey='Field type' value={FIELD_SCHEMA_TYPE_DISPLAY[type] || EMPTY_LABEL} />
      <PropertyRow
        textKey='Label'
        value={label || EMPTY_LABEL}
        changeType={changeTypeForColumn(changeColumn, fieldChanges?.['label']?.['change-type'])}
      />
      <PropertyRow
        textKey='Default value'
        value={getDefaultValueDisplay(defaultValue)}
        changeType={changeTypeForColumn(
          changeColumn,
          fieldChanges?.['default-value']?.['change-type'],
        )}
      />
      <PropertyRow
        textKey='Validations'
        value={generateValidationLabel(field)}
        changeType={changeTypeForColumn(
          changeColumn,
          fieldChanges?.['config.max-char-length']?.['change-type'],
        )}
      />
      <PropertyRow
        textKey='Redaction policy'
        value={generateRedactionPolicyLabel(redactionPolicy)}
        changeType={changeTypeForColumn(
          changeColumn,
          fieldChanges?.['config.redaction-policy']?.['change-type'],
        )}
      />
      <PropertyRow
        textKey='Write policy'
        value={generateWritePolicyLabel(writePolicy)}
        changeType={changeTypeForColumn(
          changeColumn,
          fieldChanges?.['config.write-policy']?.['change-type'],
        )}
      />
    </>
  );
}

const FieldTable = ({
  versionDiff,
  showUnchanged,
  targetInquiryTemplateVersion,
}: {
  versionDiff: VersionDiff;
  targetInquiryTemplateVersion: InquiryTemplateVersion;
  showUnchanged: boolean;
}) => {
  const { 'field-schema': fieldSchemaChanges } = versionDiff.inquiryTemplateVersionDiff;
  const targetFieldSchema = targetInquiryTemplateVersion.attributes['field-schema'];
  const baseFieldSchema = versionDiff.baseInquiryTemplateVersionData.fieldSchema;

  const targetFieldSchemaMap = keyBy('key', targetFieldSchema);
  const baseFieldSchemaMap = keyBy('key', baseFieldSchema);

  const noChanges = isEmpty(fieldSchemaChanges);

  if (noChanges && !showUnchanged) {
    return (
      <Label color='secondary' size='lg'>
        No changes made
      </Label>
    );
  }

  return (
    <Table>
      <Row>
        <Cell>
          <Label>Previous Version</Label>
        </Cell>
        <Cell>
          <Label>Current Version</Label>
        </Cell>
      </Row>
      {Object.entries(fieldSchemaChanges).map(([fieldKey, fieldChanges]) => {
        const baseField = baseFieldSchemaMap[fieldKey];
        const targetField = targetFieldSchemaMap[fieldKey];
        const tableRowWithDiffTagPartialProps: Omit<TableRowWithDiffTagProps, 'changeType'> = {
          baseNode: EMPTY_LABEL,
          targetNode: EMPTY_LABEL,
          label: '',
        };
        const fieldChangeType = fieldChanges['change-type'];

        switch (fieldChangeType) {
          case VersionDiffChangeType.ADDED:
            tableRowWithDiffTagPartialProps.label = targetField.key;
            tableRowWithDiffTagPartialProps.targetNode = (
              <FieldSchemaDisplay changeColumn={Column.TARGET} field={targetField} />
            );
            break;
          case VersionDiffChangeType.REMOVED:
            tableRowWithDiffTagPartialProps.label = baseField.key;
            tableRowWithDiffTagPartialProps.baseNode = (
              <FieldSchemaDisplay changeColumn={Column.BASE} field={baseField} />
            );
            break;
          case VersionDiffChangeType.MODIFIED: {
            tableRowWithDiffTagPartialProps.label = targetField.key;
            tableRowWithDiffTagPartialProps.targetNode = (
              <FieldSchemaDisplay
                changeColumn={Column.TARGET}
                field={targetField}
                fieldChanges={fieldChanges.changes}
              />
            );

            tableRowWithDiffTagPartialProps.baseNode = (
              <FieldSchemaDisplay
                changeColumn={Column.BASE}
                field={baseField}
                fieldChanges={fieldChanges.changes}
              />
            );
            break;
          }
          default:
            assertNever(fieldChangeType);
        }

        return (
          <TableRowWithDiffTag
            key={fieldKey}
            changeType={fieldChanges?.['change-type']}
            {...tableRowWithDiffTagPartialProps}
          />
        );
      })}
      {showUnchanged &&
        targetFieldSchema
          .filter((targetField) => !(targetField.key in fieldSchemaChanges))
          .map((field) => {
            return (
              <TableRowWithDiffTag
                changeType={undefined}
                key={field.key}
                label={field.key}
                targetNode={<FieldSchemaDisplay changeColumn={Column.TARGET} field={field} />}
                baseNode={<FieldSchemaDisplay changeColumn={Column.BASE} field={field} />}
              />
            );
          })}
    </Table>
  );
};

interface InquiryTemplateVersionDiffFieldProps {
  versionDiff: VersionDiff;
  inquiryTemplateVersion: InquiryTemplateVersion;
}

const InquiryTemplateVersionDiffField = ({
  versionDiff,
  inquiryTemplateVersion,
}: InquiryTemplateVersionDiffFieldProps) => {
  const [showUnchanged, { toggle: toggleShowUnchanged }] = useToggle(false);
  return (
    <Card>
      <Card.Body>
        <Card.Title
          actions={[
            <ToggleSlider
              key='show-unchanged-toggle'
              titleOn='displaying all values'
              titleOff='only showing changes'
              labelOn='Show unchanged'
              labelOff='Show unchanged'
              value={showUnchanged}
              onChange={toggleShowUnchanged}
            />,
          ]}
        >
          Fields
        </Card.Title>
        <FieldTable
          versionDiff={versionDiff}
          targetInquiryTemplateVersion={inquiryTemplateVersion}
          showUnchanged={showUnchanged}
        />
      </Card.Body>
    </Card>
  );
};

export default InquiryTemplateVersionDiffField;
